export class WebAuthnCeremonyError extends Error {
  constructor(message, { kind = "unavailable" } = {}) {
    super(message);
    this.name = "WebAuthnCeremonyError";
    this.kind = kind;
  }
}

function field(form, name) {
  const control = form?.elements?.namedItem?.(name)
    ?? form?.querySelector?.(`[name="${name}"]`);
  if (!control || typeof control.value !== "string") {
    throw new WebAuthnCeremonyError("WebAuthn form data is unavailable.", {
      kind: "contract",
    });
  }
  return control;
}

function jsonField(form, name) {
  const control = field(form, name);
  try {
    const value = JSON.parse(control.value);
    if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error();
    return value;
  } catch (_error) {
    throw new WebAuthnCeremonyError("WebAuthn options are invalid.", {
      kind: "contract",
    });
  }
}

function credentialJson(credential) {
  if (!credential || typeof credential.toJSON !== "function") {
    throw new WebAuthnCeremonyError("This browser cannot serialize WebAuthn credentials safely.");
  }
  return JSON.stringify(credential.toJSON());
}

async function createCredential(credentials, publicKey, signal) {
  try {
    return await credentials.create({ publicKey, signal });
  } catch (error) {
    if (error?.name === "NotSupportedError") {
      throw new WebAuthnCeremonyError(
        "This device cannot create the requested WebAuthn credential for FishyStuff.",
        { kind: "deviceUnsupported" },
      );
    }
    throw error;
  }
}

function allowPortableDiscoverableAuthenticator(publicKeyJson) {
  const selection = publicKeyJson?.authenticatorSelection;
  if (
    selection
    && typeof selection === "object"
    && !Array.isArray(selection)
    && selection.authenticatorAttachment === "platform"
  ) {
    delete selection.authenticatorAttachment;
  }
  return publicKeyJson;
}

function isPasskeyTrigger(trigger) {
  return String(trigger ?? "").startsWith("oryPasskey");
}

export function webauthnCeremonyFailureCopy(error, trigger) {
  const passkey = isPasskeyTrigger(trigger);
  if (error?.kind === "deviceUnsupported") {
    return Object.freeze({
      key: passkey
        ? "account.passkey.device_unavailable"
        : "account.webauthn.device_unavailable",
      fallback: passkey
        ? "This device can’t create a FishyStuff passkey. Try a device with a supported password manager and screen lock."
        : "This device can’t create a FishyStuff security key. Try another browser, device biometric, or hardware security key.",
    });
  }
  if (error?.kind === "unavailable") {
    return Object.freeze({
      key: passkey ? "account.passkey.unavailable" : "account.webauthn.unavailable",
      fallback: passkey
        ? "Passkey support is unavailable."
        : "Security-key support is unavailable.",
    });
  }
  return Object.freeze({
    key: passkey ? "account.passkey.cancelled" : "account.webauthn.cancelled",
    fallback: passkey
      ? "The passkey request did not complete."
      : "The security-key request did not complete.",
  });
}

function registrationOptions(form, trigger, PublicKeyCredentialClass) {
  if (trigger === "oryPasskeyRegistration") {
    const createData = jsonField(form, "passkey_create_data");
    const publicKeyJson = structuredClone(createData.credentialOptions?.publicKey);
    if (!publicKeyJson || typeof createData.displayNameFieldName !== "string") {
      throw new WebAuthnCeremonyError("Passkey registration options are invalid.", {
        kind: "contract",
      });
    }
    const configuredDisplayName = field(form, createData.displayNameFieldName).value.trim();
    const handle = field(form, "traits.username").value.trim();
    const authenticatorLabel = configuredDisplayName || handle;
    publicKeyJson.user = {
      ...publicKeyJson.user,
      name: authenticatorLabel,
      displayName: authenticatorLabel,
    };
    return PublicKeyCredentialClass.parseCreationOptionsFromJSON(
      allowPortableDiscoverableAuthenticator(publicKeyJson),
    );
  }
  if (trigger === "oryWebAuthnRegistration") {
    const options = jsonField(form, "webauthn_register_trigger");
    const publicKeyJson = structuredClone(options.publicKey);
    if (!publicKeyJson) {
      throw new WebAuthnCeremonyError("Security-key registration options are invalid.", {
        kind: "contract",
      });
    }
    return PublicKeyCredentialClass.parseCreationOptionsFromJSON(publicKeyJson);
  }
  const createData = jsonField(form, "passkey_create_data");
  const publicKeyJson = structuredClone(createData.publicKey ?? createData);
  allowPortableDiscoverableAuthenticator(publicKeyJson);
  return PublicKeyCredentialClass.parseCreationOptionsFromJSON(publicKeyJson);
}

export function webauthnCeremonyAvailable({
  credentials = globalThis.navigator?.credentials,
  PublicKeyCredentialClass = globalThis.PublicKeyCredential,
} = {}) {
  return Boolean(
    credentials
    && typeof credentials.get === "function"
    && typeof credentials.create === "function"
    && typeof PublicKeyCredentialClass?.parseRequestOptionsFromJSON === "function"
    && typeof PublicKeyCredentialClass?.parseCreationOptionsFromJSON === "function",
  );
}

export async function performWebAuthnCeremony(form, trigger, {
  credentials = globalThis.navigator?.credentials,
  PublicKeyCredentialClass = globalThis.PublicKeyCredential,
  signal,
} = {}) {
  if (!webauthnCeremonyAvailable({ credentials, PublicKeyCredentialClass })) {
    throw new WebAuthnCeremonyError("WebAuthn support is unavailable.");
  }
  if (["oryPasskeyLogin", "oryWebAuthnLogin"].includes(trigger)) {
    const challenge = jsonField(
      form,
      trigger === "oryWebAuthnLogin" ? "webauthn_login_trigger" : "passkey_challenge",
    );
    const publicKey = PublicKeyCredentialClass.parseRequestOptionsFromJSON(
      challenge.publicKey ?? challenge,
    );
    const credential = await credentials.get({ publicKey, signal });
    field(
      form,
      trigger === "oryWebAuthnLogin" ? "webauthn_login" : "passkey_login",
    ).value = credentialJson(credential);
    return Object.freeze({ kind: "ready" });
  }
  if ([
    "oryPasskeyRegistration",
    "oryPasskeySettingsRegistration",
    "oryWebAuthnRegistration",
  ].includes(trigger)) {
    const publicKey = registrationOptions(form, trigger, PublicKeyCredentialClass);
    const credential = await createCredential(credentials, publicKey, signal);
    field(
      form,
      trigger === "oryWebAuthnRegistration"
        ? "webauthn_register"
        : trigger === "oryPasskeySettingsRegistration"
          ? "passkey_settings_register"
          : "passkey_register",
    ).value = credentialJson(credential);
    return Object.freeze({ kind: "ready" });
  }
  throw new WebAuthnCeremonyError("WebAuthn action is unsupported.", {
    kind: "contract",
  });
}
